<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import AppLayout from '@/app/AppLayout.vue';
import AuthLayout from '@/app/AuthLayout.vue';
import BareLayout from '@/app/BareLayout.vue';
import ErrorBoundary from '@/app/ErrorBoundary.vue';
import GlobalLoadingBar from '@/shared/ui/GlobalLoadingBar.vue';

const route = useRoute();

/**
 * `bare` renders the view with no chrome at all: the certificate verification page is
 * meant for somebody outside the organization, and wrapping it in a navigation rail they
 * cannot use would be an invitation to a login screen they do not have.
 */
const LAYOUTS = { auth: AuthLayout, bare: BareLayout } as const;

const layout = computed(() => {
  const requested = route.meta.layout;
  return typeof requested === 'string' && requested in LAYOUTS
    ? LAYOUTS[requested as keyof typeof LAYOUTS]
    : AppLayout;
});
</script>

<template>
  <GlobalLoadingBar />

  <component :is="layout">
    <!-- WEB-007 — the boundary sits inside the layout so a failing view degrades to a
         recovery panel while the navigation stays usable. -->
    <ErrorBoundary>
      <RouterView v-slot="{ Component }">
        <!-- Keyed by path so navigating between two employee profiles genuinely
             remounts the view instead of reusing stale internal state. -->
        <component
          :is="Component"
          :key="route.path"
        />
      </RouterView>
    </ErrorBoundary>
  </component>
</template>
